import axiosClient from "./axiosClient";

const FINANCE_PAGE_SIZE = 25;

const paginatedParams = (page = 1, pageSize = FINANCE_PAGE_SIZE, extra = {}) => ({
  page,
  page_size: pageSize,
  ...extra,
});

// Dashboard
export const getFinanceDashboard = (page = 1) =>
  axiosClient.get("/finance/dashboard/", { params: paginatedParams(page) });

export const getPropertyFinanceSummary = (propertyId, page = 1) =>
  axiosClient.get(`/finance/properties/${propertyId}/summary/`, {
    params: paginatedParams(page),
  });

export const getServiceChargeSettings = (propertyId) =>
  axiosClient.get(`/finance/properties/${propertyId}/service-charge-settings/`);

export const updateServiceChargeMechanism = (propertyId, data) =>
  axiosClient.patch(`/finance/properties/${propertyId}/service-charge-settings/`, data);

export const getEstateCodePrefix = () =>
  axiosClient.get("/finance/settings/estate-code-prefix/");

export const updateEstateCodePrefix = (data) =>
  axiosClient.patch("/finance/settings/estate-code-prefix/", data);

export const updateFiscalYear = (propertyId, fiscalYearId, data) =>
  axiosClient.patch(
    `/finance/properties/${propertyId}/fiscal-years/${fiscalYearId}/`,
    data
  );

export const getServiceChargeRecalculationPreview = (propertyId) =>
  axiosClient.get(`/finance/properties/${propertyId}/service-charge-recalculation/`);

export const applyServiceChargeRecalculation = (propertyId) =>
  axiosClient.post(`/finance/properties/${propertyId}/service-charge-recalculation/apply/`);

export const getServiceChargeUpdateHistory = (propertyId, page = 1) =>
  axiosClient.get(`/finance/properties/${propertyId}/service-charge-updates/`, {
    params: paginatedParams(page),
  });

// Finance catalogue settings (admin)
export const getChartOfAccountsSettings = (params = {}) =>
  axiosClient.get("/finance/settings/chart-of-accounts/", {
    params: { all: true, include_inactive: true, ...params },
  });

export const createChartAccount = (data) =>
  axiosClient.post("/finance/settings/chart-of-accounts/", data);

export const updateChartAccount = (accountId, data) =>
  axiosClient.put(`/finance/settings/chart-of-accounts/${accountId}/`, data);

export const deleteChartAccount = (accountId) =>
  axiosClient.delete(`/finance/settings/chart-of-accounts/${accountId}/`);

export const getChartOfAccountsLookup = () =>
  axiosClient.get("/finance/chart-of-accounts/");

export const getBudgetLineItemsSettings = (params = {}) =>
  axiosClient.get("/finance/settings/budget-line-items/", {
    params: { all: true, include_inactive: true, ...params },
  });

export const createBudgetLineItemSetting = (data) =>
  axiosClient.post("/finance/settings/budget-line-items/", data);

export const updateBudgetLineItemSetting = (itemId, data) =>
  axiosClient.put(`/finance/settings/budget-line-items/${itemId}/`, data);

export const deleteBudgetLineItemSetting = (itemId) =>
  axiosClient.delete(`/finance/settings/budget-line-items/${itemId}/`);

export const getBudgetLineItemCategories = () =>
  axiosClient.get("/finance/settings/budget-line-item-categories/");

// Budget catalogue
export const getBudgetLineItems = () => axiosClient.get("/finance/budget-line-items/");

// Fiscal years
export const getFiscalYears = (propertyId) =>
  axiosClient.get(`/finance/properties/${propertyId}/fiscal-years/`);

export const createFiscalYear = (propertyId, data) =>
  axiosClient.post(`/finance/properties/${propertyId}/fiscal-years/`, data);

// Budget templates
export const getBudgetTemplates = (propertyId) =>
  axiosClient.get(`/finance/properties/${propertyId}/budget-templates/`);

export const createBudgetTemplate = (propertyId, data) =>
  axiosClient.post(`/finance/properties/${propertyId}/budget-templates/`, data);

export const updateBudgetTemplate = (propertyId, templateId, data) =>
  axiosClient.put(`/finance/properties/${propertyId}/budget-templates/${templateId}/`, data);

// Budgets
export const getBudgets = (propertyId, page = 1, params = {}) =>
  axiosClient.get(`/finance/properties/${propertyId}/budgets/`, {
    params: paginatedParams(page, FINANCE_PAGE_SIZE, params),
  });

export const getBudget = (propertyId, budgetId) =>
  axiosClient.get(`/finance/properties/${propertyId}/budgets/${budgetId}/`);

export const createBudget = (propertyId, data) =>
  axiosClient.post(`/finance/properties/${propertyId}/budgets/`, data);

export const updateBudget = (propertyId, budgetId, data) =>
  axiosClient.put(`/finance/properties/${propertyId}/budgets/${budgetId}/`, data);

export const deleteBudget = (propertyId, budgetId) =>
  axiosClient.delete(`/finance/properties/${propertyId}/budgets/${budgetId}/`);

export const updateBudgetStatus = (propertyId, budgetId, data) =>
  axiosClient.post(`/finance/properties/${propertyId}/budgets/${budgetId}/status/`, data);

export const updateSectionAllocations = (propertyId, budgetId, sectionId, data) =>
  axiosClient.put(
    `/finance/properties/${propertyId}/budgets/${budgetId}/sections/${sectionId}/allocations/`,
    data
  );

export const getApportionmentChecks = (propertyId, budgetId) =>
  axiosClient.get(
    `/finance/properties/${propertyId}/budgets/${budgetId}/apportionment-checks/`
  );

// Owner invoices
export const getOwnerInvoices = (propertyId, page = 1, params = {}) =>
  axiosClient.get(`/finance/properties/${propertyId}/owner-invoices/`, {
    params: paginatedParams(page, FINANCE_PAGE_SIZE, params),
  });

export const getOwnerInvoice = (propertyId, invoiceId) =>
  axiosClient.get(`/finance/properties/${propertyId}/owner-invoices/${invoiceId}/`);

export const getCurrentInvoicePreview = (propertyId) =>
  axiosClient.get(`/finance/properties/${propertyId}/owner-invoices/preview/`);

export const generateCurrentInvoices = (propertyId, data) =>
  axiosClient.post(`/finance/properties/${propertyId}/owner-invoices/generate/`, data);

export const postAllCurrentDraftInvoices = (propertyId) =>
  axiosClient.post(`/finance/properties/${propertyId}/owner-invoices/post-all-drafts/`, {});

export const getInvoicePreview = (propertyId, budgetId) =>
  axiosClient.get(
    `/finance/properties/${propertyId}/budgets/${budgetId}/invoice-preview/`
  );

export const generateInvoices = (propertyId, budgetId, data) =>
  axiosClient.post(
    `/finance/properties/${propertyId}/budgets/${budgetId}/generate-invoices/`,
    data
  );

export const postOwnerInvoice = (propertyId, invoiceId, rowVersion) =>
  axiosClient.post(
    `/finance/properties/${propertyId}/owner-invoices/${invoiceId}/post/`,
    { row_version: rowVersion }
  );

export const generateOwnerInvoicePdf = (propertyId, invoiceId) =>
  axiosClient.post(
    `/finance/properties/${propertyId}/owner-invoices/${invoiceId}/generate-pdf/`
  );

export const postAllDraftInvoices = (propertyId, budgetId) =>
  axiosClient.post(
    `/finance/properties/${propertyId}/budgets/${budgetId}/post-all-invoices/`,
    {}
  );

export const getOwnerPayments = (propertyId, page = 1, params = {}) =>
  axiosClient.get(`/finance/properties/${propertyId}/owner-payments/`, {
    params: paginatedParams(page, FINANCE_PAGE_SIZE, params),
  });

export const createOwnerPayment = (propertyId, data) =>
  axiosClient.post(`/finance/properties/${propertyId}/owner-payments/`, data);

// Suppliers
export const getSuppliers = (page = 1, params = {}) =>
  axiosClient.get("/finance/suppliers/", {
    params: paginatedParams(page, FINANCE_PAGE_SIZE, params),
  });

export const createSupplier = (data) => axiosClient.post("/finance/suppliers/", data);

export const updateSupplier = (supplierId, data) =>
  axiosClient.put(`/finance/suppliers/${supplierId}/`, data);

export const getSupplierInvoices = (propertyId, page = 1, params = {}) =>
  axiosClient.get(`/finance/properties/${propertyId}/supplier-invoices/`, {
    params: paginatedParams(page, FINANCE_PAGE_SIZE, params),
  });

export const createSupplierInvoice = (propertyId, data) =>
  axiosClient.post(`/finance/properties/${propertyId}/supplier-invoices/`, data);

export const approveSupplierInvoice = (propertyId, invoiceId, rowVersion) =>
  axiosClient.post(
    `/finance/properties/${propertyId}/supplier-invoices/${invoiceId}/approve/`,
    { row_version: rowVersion }
  );

export const getSupplierPayments = (propertyId, page = 1) =>
  axiosClient.get(`/finance/properties/${propertyId}/supplier-payments/`, {
    params: paginatedParams(page),
  });

// Bank
export const getBankAccounts = (propertyId, page = 1) =>
  axiosClient.get(`/finance/properties/${propertyId}/bank-accounts/`, {
    params: paginatedParams(page),
  });

export const createBankAccount = (propertyId, data) =>
  axiosClient.post(`/finance/properties/${propertyId}/bank-accounts/`, data);

export const importBankCsv = (propertyId, accountId, file) => {
  const formData = new FormData();
  formData.append("file", file);
  return axiosClient.post(
    `/finance/properties/${propertyId}/bank-accounts/${accountId}/import-csv/`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
};

export const getBankStatementLines = (propertyId, page = 1, params = {}) =>
  axiosClient.get(`/finance/properties/${propertyId}/bank-statement-lines/`, {
    params: paginatedParams(page, FINANCE_PAGE_SIZE, params),
  });

export const getBankStatementLine = (propertyId, lineId) =>
  axiosClient.get(
    `/finance/properties/${propertyId}/bank-statement-lines/${lineId}/`
  );

export const reconcileOwnerPayment = (propertyId, lineId, data) =>
  axiosClient.post(
    `/finance/properties/${propertyId}/bank-statement-lines/${lineId}/reconcile-owner/`,
    data
  );

export const reconcileSupplierPayment = (propertyId, lineId, data) =>
  axiosClient.post(
    `/finance/properties/${propertyId}/bank-statement-lines/${lineId}/reconcile-supplier/`,
    data
  );

export const ignoreStatementLine = (propertyId, lineId, rowVersion) =>
  axiosClient.post(
    `/finance/properties/${propertyId}/bank-statement-lines/${lineId}/ignore/`,
    { row_version: rowVersion }
  );

export const unignoreStatementLine = (propertyId, lineId, rowVersion) =>
  axiosClient.post(
    `/finance/properties/${propertyId}/bank-statement-lines/${lineId}/unignore/`,
    { row_version: rowVersion }
  );

// Statements & ledger
export const getUnitStatement = (unitId, params = {}) =>
  axiosClient.get(`/finance/units/${unitId}/statement/`, { params });

export const getPropertyJournals = (propertyId, page = 1, params = {}) =>
  axiosClient.get(`/finance/properties/${propertyId}/journals/`, {
    params: paginatedParams(page, FINANCE_PAGE_SIZE, params),
  });

export const getPropertyLedgerEntries = (propertyId, page = 1, params = {}) =>
  axiosClient.get(`/finance/properties/${propertyId}/ledger-entries/`, {
    params: paginatedParams(page, FINANCE_PAGE_SIZE, params),
  });

export const getDiscretionaryAllocations = (propertyId, budgetId, sectionId, params) =>
  axiosClient.get(`/finance/properties/${propertyId}/budgets/${budgetId}/sections/${sectionId}/discretionary-allocations/`, { params });

export const saveDiscretionaryAllocations = (propertyId, budgetId, sectionId, data, params) =>
  axiosClient.put(`/finance/properties/${propertyId}/budgets/${budgetId}/sections/${sectionId}/discretionary-allocations/`, data, { params });

export const getSupplier = (id) => axiosClient.get(`/finance/suppliers/${id}/`);
export const getSupplierRecords = (id, kind, page, pageSize, search = "", filters = {}) => axiosClient.get(id == null ? `/finance/suppliers/${kind}/` : `/finance/suppliers/${id}/${kind}/`, { params: { page, page_size: pageSize, search, ...filters } });

export const getSupplierProperties = (search, page) => axiosClient.get("/finance/suppliers/properties/", { params: { search, page, page_size: 10 } });
